// BUILD panel — toggled by the BUILD rail button. Lists building types with
// cost, owned count, and a BUY button per type. Mirrors goonsPanel's shape.

import { Events, on, emit } from './eventBus.js'
import { BuildingConfig, BuildingTypes, PlotConfig, PlotTypes, HomeLevels, RealEstatePrices, RicePaddyPrice } from '../config/balance.js'
import { freeTilesRemaining, canAfford, priceOf } from '../game/buildingManager.js'
import { getBuildings } from '../game/buildingState.js'
import { getHomeLevel, upgradeHome, countUndevelopedPlots, countByType as countPlotsByType } from '../game/plotState.js'
import { spendWealth, getWealth } from '../game/seasonManager.js'
import { makeModalToggleable } from './railPanel.js'
import { pushLog } from './hud.js'
import { formatCoins, cantAfford } from './format.js'
import { appendBuyCoinsLink } from './buyCoinsLink.js'

const NEED_PLOT_MSG = 'You must have an available plot to place a building. Buy a plot of land first.'
const NO_PLOT_MSG = 'You must purchase land first — tap “＋ BUY LAND PLOT,” claim a tile, then place your building on it.'

export function mountBuildPanel({ root, toggleBtn }) {
  root.innerHTML = `
    <div class="hud__build-header">
      <span class="hud__build-title">BUILD</span>
      <span class="hud__build-tiles" data-plots>—</span>
    </div>
    <div class="hud__build-notice" data-notice role="alert" hidden></div>
    <button class="hud__land-buy" data-buy-land>
      <span class="lbl">＋ BUY LAND PLOT</span>
      <span class="sub">claim a tile — cheaper toward the edges</span>
    </button>
    <div class="hud__build-list">
      <button class="hud__build-buy hud__home-upgrade" data-home-upgrade hidden></button>
      <div class="hud__build-row" data-rice-row>
        <div class="hud__build-info">
          <div class="hud__build-name">Rice Paddy</div>
          <div class="hud__build-desc">Income land — earns Mao Coin every harvest</div>
          <div class="hud__build-zone">📍 On one of your plots</div>
        </div>
        <div class="hud__build-owned" data-rice-owned>0</div>
        <button class="hud__build-buy" data-buy-rice>
          <span class="lbl">PLACE</span>
          <span class="price">${formatCoins(RicePaddyPrice)}</span>
        </button>
      </div>
      ${Object.values(BuildingTypes).map(rowTemplate).join('')}
    </div>
  `
  appendBuyCoinsLink(root) // out of coin? a quiet link to the Shop

  // "＋ Buy Land Plot" → enter claim mode (always available; price is per-tile).
  root.querySelector('[data-buy-land]').addEventListener('click', () => {
    emit(Events.PlacementStart, { type: 'LAND' })
  })

  const noticeEl = root.querySelector('[data-notice]')
  const landBtn = root.querySelector('[data-buy-land]')
  let noticeTimer = null
  // Explain WHY a building can't be placed, and nudge the BUY LAND PLOT button (a short shake + glow).
  const needPlot = (msg) => {
    noticeEl.textContent = msg
    noticeEl.hidden = false
    landBtn.classList.remove('hud__land-buy--attn')
    void landBtn.offsetWidth // restart the animation
    landBtn.classList.add('hud__land-buy--attn')
    clearTimeout(noticeTimer)
    noticeTimer = setTimeout(() => { noticeEl.hidden = true; landBtn.classList.remove('hud__land-buy--attn') }, 5000)
  }
  // Picking something to place closes this pop-up with a short fade, then the yellow-square cursor takes over.
  const startPlacing = (type) => {
    noticeEl.hidden = true
    root.classList.add('hud__modal--fading')
    setTimeout(() => { root.classList.remove('open', 'hud__modal--fading') }, 220)
    emit(Events.PlacementStart, { type })
  }

  // Rice paddy — needs an owned, undeveloped plot to sit on.
  root.querySelector('[data-buy-rice]').addEventListener('click', () => {
    if (countUndevelopedPlots() <= 0) { needPlot(NEED_PLOT_MSG); return }
    if (getWealth() < RicePaddyPrice) { needPlot(cantAfford('lay a rice paddy', RicePaddyPrice, getWealth())); return }
    startPlacing('RICE_PADDY')
  })
  const riceOwnedEl = root.querySelector('[data-rice-owned]')

  const rows = new Map()
  for (const id of Object.values(BuildingTypes)) {
    const row = root.querySelector(`[data-building="${id}"]`)
    rows.set(id, {
      ownedEl: row.querySelector('[data-owned]'),
      buyEl: row.querySelector('[data-buy]'),
    })
    row.querySelector('[data-buy]').addEventListener('click', () => {
      // Gate: you can't place a building with no land to put it on (or no coin to pay for it).
      if (countUndevelopedPlots() <= 0) { needPlot(NEED_PLOT_MSG); return }
      if (!canAfford(id)) { needPlot(cantAfford(`build a ${BuildingConfig[id].name}`, priceOf(id), getWealth())); return }
      startPlacing(id)
    })
  }

  const plotsEl = root.querySelector('[data-plots]')

  // Home upgrade (CR-5): shack → middle-class manor, bought once. Instant (no
  // tile placement — it swaps the home diorama in place).
  const upgradeEl = root.querySelector('[data-home-upgrade]')
  upgradeEl.addEventListener('click', () => {
    const res = upgradeHome(spendWealth)
    if (res.ok) pushLog(`🏠 Home upgraded to a middle-class manor for ${formatCoins(res.cost)}.`)
    else if (res.reason === 'not enough coin') pushLog(cantAfford('upgrade your home', RealEstatePrices.HOME_MIDDLE, getWealth()))
    refresh()
  })

  const refresh = () => {
    const plots = countUndevelopedPlots()
    plotsEl.textContent = `${plots} undeveloped plot${plots === 1 ? '' : 's'}`
    plotsEl.classList.toggle('active', plots > 0)
    if (riceOwnedEl) riceOwnedEl.textContent = String(countPlotsByType(PlotTypes.FARM))
    root.querySelector('[data-rice-row]')?.classList.toggle('hud__build-row--locked', plots <= 0 || getWealth() < RicePaddyPrice)
    const ownedByType = countByType(getBuildings())
    for (const [id, els] of rows) {
      els.ownedEl.textContent = String(ownedByType.get(id) ?? 0)
      els.buyEl.disabled = freeTilesRemaining() <= 0 // never fully disabled for plot/coin: clicking explains what's missing
      els.buyEl.closest('.hud__build-row')?.classList.toggle('hud__build-row--locked', plots <= 0 || !canAfford(id))
    }
    // Show the upgrade button only while still a shack.
    const isShack = getHomeLevel() === HomeLevels.SHACK
    upgradeEl.hidden = !isShack
    if (isShack) {
      upgradeEl.innerHTML = `<span class="lbl">⬆ UPGRADE HOME → MANOR</span><span class="price">${formatCoins(RealEstatePrices.HOME_MIDDLE)}</span>`
      upgradeEl.disabled = getWealth() < RealEstatePrices.HOME_MIDDLE
    }
  }

  on(Events.WealthChange, refresh)
  on(Events.BuildingAdded, refresh)
  on(Events.HomeLevelChange, refresh)
  on(Events.PlotChanged, refresh)   // claims + zoning change the plot count
  on(Events.KingdomChange, refresh)

  makeModalToggleable(root, toggleBtn)
  refresh()
}

function countByType(buildings) {
  const m = new Map()
  for (const b of buildings) m.set(b.type, (m.get(b.type) ?? 0) + 1)
  return m
}

// Readable list of the zones a building may sit on (mirrors placement.js).
function zoneLabel(id) {
  const allowed = BuildingConfig[id]?.allowedPlotTypes
  if (!allowed?.length) return 'Any owned plot'
  return allowed.map((z) => PlotConfig[z]?.name ?? z).join(' / ')
}

function rowTemplate(id) {
  const cfg = BuildingConfig[id]
  return `
    <div class="hud__build-row" data-building="${id}">
      <div class="hud__build-info">
        <div class="hud__build-name">${cfg.name}</div>
        <div class="hud__build-desc">${cfg.description}</div>
        <div class="hud__build-zone">📍 ${zoneLabel(id)}</div>
      </div>
      <div class="hud__build-owned" data-owned>0</div>
      <button class="hud__build-buy" data-buy>
        <span class="lbl">BUILD</span>
        <span class="price">${formatCoins(priceOf(id))}</span>
      </button>
    </div>
  `
}
